import { Component } from "react";
import { canAbsorbPanelWheel } from "../panel-scroll";
import { AUDIO_MODE_PROPS, buildAudioModes } from "./audio-modes";
import { popupComponentDefaultProps } from "./default-props";
import ActionsFooter from "./actions-footer";
import DebugBar from "./debug-bar";
import EffectsSection from "./effects-section";
import PopupHeader from "./popup-header";
import { isPromoQualified } from "../rate-prompt";
import Mood from "./mood";
import SkinSheet from "./skin-sheet";
import HelpSheet from "./help-sheet";
import { areControlsAtDefaults, shouldShowResetAll } from "./reset-all";
import FeedbackSheet from "./feedback-sheet";
import StudioSheet from "./studio-sheet";
import BillingSheet from "./billing-sheet";
import UnsupportedGate from "./unsupported-gate";
import SlowControlSection from "./slow-control-section";
import { isSafari } from "../../platform";
import {
  hasShuffleSyncPropsChanged,
  seedSliderTouchedKeys,
  sliderFillPct,
  sliderMidValue,
  sliderValueFromClientX,
} from "./tile-slider";

const TILE_VALUE_HOLD_MS = 300;
// The fade over the controls while a sheet is up (.content__push-shell::before).
const SHEET_FADE_ROOM = 40;

// An element's exact layout height: offsetHeight rounds to whole pixels, and
// getBoundingClientRect includes transforms, so the element's own scale
// (.content--disabled) is divided back out.
function layoutHeight(el) {
  if (!el) return 0;
  const { height } = el.getBoundingClientRect();
  const transform = getComputedStyle(el).transform;
  let scaleY = 1;
  if (transform && transform !== "none" && typeof DOMMatrixReadOnly === "function") {
    scaleY = new DOMMatrixReadOnly(transform).d;
  }
  return scaleY > 0 ? height / scaleY : height;
}

const TILE_VALUE_FADE_MS = 300;

// The promo nudges' marks (popup/promo-nudges.js), on the same 12x12 grid as
// the rating stars: a play triangle, a speech bubble, a star, a share arrow.
const PROMO_NUDGE_ICONS = {
  youtube: <path d="M3 1.5v9l7.5-4.5z" />,
  feedback: (
    <path d="M2 1.5h8A1.5 1.5 0 0 1 11.5 3v4A1.5 1.5 0 0 1 10 8.5H5.5L2.8 10.8V8.5H2A1.5 1.5 0 0 1 .5 7V3A1.5 1.5 0 0 1 2 1.5z" />
  ),
  review: (
    <path d="M6 .8l1.6 3.3 3.6.5-2.6 2.5.6 3.6L6 9l-3.2 1.7.6-3.6L.8 4.6l3.6-.5z" />
  ),
  share: (
    <path d="M7.2 1.2L11.4 5 7.2 8.8V6.6C4.4 6.6 2.3 7.6 1 9.9c.3-3.4 2.4-5.9 6.2-6.3z" />
  ),
};

class PopupComponent extends Component {
  constructor(props) {
    super(props);

    this.sliderTouched = {};
    this.tileDrag = null;
    this.suppressAdjustableClick = false;
    this.state = {
      mount: false,
      draggingTileKey: null,
      holdingTileKey: null,
      revealingTileKey: null,
      releasingTileKey: null,
      fillGrowingKeys: {},
      tileSliderOverrides: {},
      shuffleBusy: false,
      subscriptionStatusStep: 0,
      openSheet: null,
      contentPushHeight: null,
      // The open sheet's own height, with room for the fade over it: the
      // least the area behind a sheet is pinned to (syncSheetMinHeight).
      sheetMinHeight: null,
      moodScrimTop: null,
      moodScrimOpacity: 0,
    };
    this.contentPushRef = null;
    this.contentEffectsRef = null;
  }

  componentDidMount() {
    this.syncContentPushHeight();
    this.contentEffectsResizeObserver = new ResizeObserver(() =>
      this.syncContentPushHeight(),
    );
    if (this.contentEffectsRef) {
      this.contentEffectsResizeObserver.observe(this.contentEffectsRef);
    }
    setTimeout(() => this.setState({ mount: true }), 200);
    this.scheduleSubscriptionStatusRotation();
    this.seedSliderTouched(this.props);
    this.openInitialSheet();
  }

  // Demo pages only (popup/demo): open a sheet on load through the normal
  // open path. The real popup never passes `initialSheet`.
  openInitialSheet() {
    const { initialSheet } = this.props;
    if (initialSheet) this.setSheetOpen(initialSheet);
  }

  componentDidUpdate(prevProps, prevState) {
    this.closeSheetWhenSwitchedOff(prevProps);
    this.componentDidUpdateSheet(prevState);
    this.syncSheetMinHeight();
    this.clearSettledTileSliderOverrides();

    const statusChanged =
      prevProps.trialMode !== this.props.trialMode ||
      prevProps.remainingTrialDays !== this.props.remainingTrialDays ||
      prevProps.isPro !== this.props.isPro ||
      prevProps.usageAvailable !== this.props.usageAvailable ||
      prevProps.trialEnded !== this.props.trialEnded ||
      prevProps.usageHit !== this.props.usageHit ||
      prevProps.usagePromo !== this.props.usagePromo ||
      prevProps.weeklyCapCount !== this.props.weeklyCapCount;
    if (statusChanged) this.scheduleSubscriptionStatusRotation();

    AUDIO_MODE_PROPS.forEach(({ key, prop, isActive }) => {
      const wasActive = isActive ? isActive(prevProps) : prevProps[prop];
      const isActiveNow = isActive ? isActive(this.props) : this.props[prop];
      if (!wasActive && isActiveNow) {
        if (!this.state.fillGrowingKeys[key]) {
          this.startFillGrow(key);
        }
      } else if (wasActive && !isActiveNow) {
        this.clearFillGrow(key);
      }
    });

    if (hasShuffleSyncPropsChanged(prevProps, this.props)) {
      this.seedSliderTouched(this.props);
    }
  }

  // Switched off, an open sheet closes with the controls it sits over.
  closeSheetWhenSwitchedOff(prevProps) {
    const isOff = (props) => !props.isSlowedEnabled && !props.musicProOnly;
    if (!isOff(prevProps) && isOff(this.props) && this.state.openSheet) {
      this.setSheetOpen(null);
    }
  }

  componentDidUpdateSheet(prevState) {
    if (prevState.openSheet !== this.state.openSheet) {
      this.syncSheetScrollLock();
      // Optional observer (demo pages); the real popup passes none.
      this.props.onSheetChange?.(this.state.openSheet);
      // The scrim belongs to whichever sheet is open, so clearing it here —
      // and not only from the scroll handler — is what stops a close that
      // never got to rewind from leaving an empty band over the controls.
      if (!this.state.openSheet) this.clearMoodScrim();
    }
  }

  clearMoodScrim() {
    const { moodScrimTop, moodScrimOpacity } = this.state;
    if (moodScrimTop === null && moodScrimOpacity === 0) return;
    this.setState({ moodScrimTop: null, moodScrimOpacity: 0 });
  }

  // A sheet slides up over the controls and is pinned to the window, so
  // scrolling the popup behind it just carried the controls out from under it.
  // Freeze the document's vertical scroll for as long as one is open, and give
  // it straight back when it closes. The sheets' own chip rows scroll
  // sideways in their own containers and are untouched by this.
  syncSheetScrollLock(openSheet = this.state.openSheet) {
    if (typeof document === "undefined") return;

    const locked = !!openSheet;
    const overflow = locked ? "hidden" : "";
    if (document.documentElement) {
      document.documentElement.style.overflowY = overflow;
    }
    if (document.body) document.body.style.overflowY = overflow;

    if (locked === !!this.sheetWheelBlocker) return;

    if (locked) {
      // overflow: hidden stops the scrollbar and the wheel, but not Mac
      // trackpad inertia — the detached panel already has to cancel wheels
      // outright for the same reason. The chip rows are the one exception:
      // they scroll sideways and belong to the sheet itself.
      this.sheetWheelBlocker = (event) => {
        const chipRow = event.target?.closest?.(".mood__chips");
        if (canAbsorbPanelWheel(chipRow, event)) return;
        event.preventDefault();
      };
      document.addEventListener("wheel", this.sheetWheelBlocker, {
        passive: false,
        capture: true,
      });
      return;
    }

    document.removeEventListener("wheel", this.sheetWheelBlocker, {
      capture: true,
    });
    this.sheetWheelBlocker = null;
  }

  startFillGrow(key) {
    this.setState(
      (state) => ({
        fillGrowingKeys: { ...state.fillGrowingKeys, [key]: true },
      }),
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.clearFillGrow(key);
          });
        });
      },
    );
  }

  clearFillGrow(key) {
    if (!this.state.fillGrowingKeys[key]) return;
    this.setState((state) => {
      const fillGrowingKeys = { ...state.fillGrowingKeys };
      delete fillGrowingKeys[key];
      return { fillGrowingKeys };
    });
  }

  componentWillUnmount() {
    clearTimeout(this.subscriptionStatusTimer);
    clearTimeout(this.releaseTileTimer);
    clearTimeout(this.sheetCloseFallbackTimer);
    this.contentEffectsResizeObserver?.disconnect();
    this.teardownMoodScroll();
    // Never leave the document frozen behind a popup that is going away.
    this.syncSheetScrollLock(null);
    this.unmounted = true;
    this.tileDrag = null;
  }

  // Remember .content__effects height while no sheet is open. The pin is
  // applied only after a sheet opens so a short measurement cannot clip
  // Ambient Effects (the last row) against overflow:hidden.
  // To the fraction: a rounded pin moved the footer 1px when a sheet opened.
  syncContentPushHeight() {
    if (this.state.openSheet) return;
    const next = layoutHeight(this.contentEffectsRef);
    if (!next) return;
    if (this.state.contentPushHeight === next) return;
    this.setState({ contentPushHeight: next });
  }

  // A sheet opens into the area the controls take, pinned at their height.
  // .content__effects has a min-height that fits every sheet, so the panel
  // keeps its height when one opens (content.css). This is the fallback for
  // a sheet that ever outgrows it: the pin is never less than the sheet
  // itself plus its margin and the fade, rather than the sheet riding up
  // under the fade.
  syncSheetMinHeight() {
    let next = null;
    if (this.state.openSheet) {
      const sheet = document.getElementById("mood-sheet");
      if (sheet) {
        const margin = parseFloat(getComputedStyle(sheet).marginTop) || 0;
        next = Math.ceil(sheet.offsetHeight + margin + SHEET_FADE_ROOM);
      }
    }
    if (next === this.state.sheetMinHeight) return;
    this.setState({ sheetMinHeight: next });
  }

  teardownMoodScroll() {
    this.sheetScrollGen = (this.sheetScrollGen || 0) + 1;
    this.sheetOverflowObserver?.disconnect();
    this.sheetOverflowObserver = null;
    (this.sheetScrollRetryTimers || []).forEach(clearTimeout);
    this.sheetScrollRetryTimers = [];
    const el = this.contentPushRef;
    clearTimeout(this.moodScrollFallbackTimer);
    this.moodScrollFallbackTimer = null;
    if (!el) return;
    if (this.onMoodScroll) {
      el.removeEventListener("scroll", this.onMoodScroll);
      this.onMoodScroll = null;
    }
    if (this.onMoodScrollEnd) {
      el.removeEventListener("scrollend", this.onMoodScrollEnd);
      this.onMoodScrollEnd = null;
    }
  }

  syncMoodScrimFromScroll() {
    if (this.unmounted) return;
    const el = this.contentPushRef;
    if (!el) return;

    // No sheet open means no scrim, whatever scrollTop still says. Reading
    // the scroll position alone stranded it at full opacity whenever a close
    // could not rewind first: the detached panel keeps running behind a tab
    // the user just left, and a hidden document freezes smooth scrolling, so
    // the sheet unmounted with the panel still scrolled to the bottom.
    const max = Math.max(0, el.scrollHeight - el.clientHeight);
    const progress = !this.state.openSheet
      ? 0
      : max <= 0
        ? 1
        : Math.min(1, Math.max(0, el.scrollTop / max));

    const root = document.getElementById("Slowed");
    const mood = document.getElementById("mood-sheet");
    let top = this.state.moodScrimTop;
    if (root && mood && progress > 0) {
      top = Math.max(
        0,
        Math.round(
          mood.getBoundingClientRect().top - root.getBoundingClientRect().top,
        ),
      );
    } else if (progress <= 0) {
      top = null;
    }

    if (
      top !== this.state.moodScrimTop ||
      progress !== this.state.moodScrimOpacity
    ) {
      this.setState({ moodScrimTop: top, moodScrimOpacity: progress });
    }
  }

  // `kind` is one of "mood" | "skin" | "help" | "feedback" | "studio" |
  // "billing", or null to close.
  setSheetOpen(kind) {
    this.teardownMoodScroll();
    // A close/swap already in flight must not land on the new sheet.
    clearTimeout(this.sheetCloseFallbackTimer);

    const el = this.contentPushRef;
    if (!el) {
      this.setState({ openSheet: kind || null });
      return;
    }

    const runScroll = (targetTop, onDone) => {
      // Opening mounts the sheet under a height pin + overflow clip. The
      // destination can still move if the sheet grows after layout, but
      // restarting a smooth scroll just because we have not arrived yet
      // makes the panel hitch at the start and again at the end.
      const gen = this.sheetScrollGen;
      const started = performance.now();
      const opening = targetTop !== 0;
      const deadline = opening ? 1200 : 800;
      let startedScroll = false;
      let aimedTop = null;

      const currentTop = () => {
        const push = this.contentPushRef;
        const max = Math.max(0, push.scrollHeight - push.clientHeight);
        return Math.max(0, Math.min(targetTop, max));
      };

      const scrollToTarget = (top) => {
        const push = this.contentPushRef;
        if (!push) return;
        if (aimedTop != null && Math.abs(aimedTop - top) < 1) return;
        aimedTop = top;
        clearTimeout(this.moodScrollFallbackTimer);
        this.moodScrollFallbackTimer = setTimeout(finish, 450);
        if (Math.abs(push.scrollTop - top) < 1) {
          finish();
          return;
        }
        push.scrollTo({ top, behavior: "smooth" });
      };

      const finish = () => {
        if (this.onMoodScrollEnd !== finish) return;
        const push = this.contentPushRef;
        const top = push ? currentTop() : 0;
        // Chase only when the destination itself moved (sheet grew), not
        // while a smooth scroll is still travelling toward the same target.
        if (
          opening &&
          push &&
          performance.now() - started < deadline &&
          aimedTop != null &&
          top > aimedTop + 1
        ) {
          scrollToTarget(top);
          return;
        }
        this.syncMoodScrimFromScroll();
        this.teardownMoodScroll();
        onDone?.();
      };

      const attempt = () => {
        if (this.unmounted || this.sheetScrollGen !== gen) return;
        const push = this.contentPushRef;
        if (!push) return;

        if (startedScroll) {
          scrollToTarget(currentTop());
          return;
        }

        const max = Math.max(0, push.scrollHeight - push.clientHeight);
        if (opening && max <= 0) {
          if (performance.now() - started < deadline) {
            requestAnimationFrame(attempt);
          }
          return;
        }

        startedScroll = true;
        this.onMoodScroll = () => this.syncMoodScrimFromScroll();
        this.onMoodScrollEnd = finish;
        push.addEventListener("scroll", this.onMoodScroll);
        push.addEventListener("scrollend", this.onMoodScrollEnd);
        scrollToTarget(Math.max(0, Math.min(targetTop, max)));
      };

      if (opening && this.contentPushRef) {
        this.sheetOverflowObserver = new ResizeObserver(() => attempt());
        this.sheetOverflowObserver.observe(this.contentPushRef);
        const sheet = document.getElementById("mood-sheet");
        if (sheet) this.sheetOverflowObserver.observe(sheet);
      }
      this.sheetScrollRetryTimers = [32, 80, 160, 320, 480].map((ms) =>
        setTimeout(attempt, ms),
      );
      requestAnimationFrame(attempt);
    };

    const openSheet = (next) => {
      this.setState({ openSheet: next }, () => {
        runScroll(Number.POSITIVE_INFINITY);
      });
    };

    // Runs `after` once the panel is back at the top, or on a hard fallback
    // if scrollend never lands — the sheet must never get stuck part-way.
    const rewindThen = (after) => {
      let done = false;
      const finish = () => {
        if (done || this.unmounted) return;
        done = true;
        clearTimeout(this.sheetCloseFallbackTimer);
        after();
      };
      this.sheetCloseFallbackTimer = setTimeout(finish, 600);
      runScroll(0, finish);
    };

    if (!kind) {
      // Scroll up first while layout stays put, then clear open state.
      rewindThen(() => this.setState({ openSheet: null }));
      return;
    }

    // Another sheet is already open: swap content in place. Rewinding and
    // reopening replayed the whole panel motion (and the sheet mount fade
    // twice), which feels like the nav closed and opened again. Keep the
    // height pin, snap to the new sheet's bottom, and let `.mood--open`
    // animate only the incoming content.
    if (this.state.openSheet && this.state.openSheet !== kind) {
      this.setState({ openSheet: kind }, () => {
        const push = this.contentPushRef;
        if (!push) return;
        const parkOnSheet = () => {
          if (this.unmounted || !this.contentPushRef) return;
          const el = this.contentPushRef;
          el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
          this.syncMoodScrimFromScroll();
        };
        parkOnSheet();
        requestAnimationFrame(parkOnSheet);
      });
      return;
    }

    openSheet(kind);
  }

  // The PRO button: the Billing sheet, read afresh each time it opens.
  openBilling() {
    const opening = this.state.openSheet !== "billing";
    this.setSheetOpen(opening ? "billing" : null);
    if (opening) this.props.onBillingOpen?.();
  }

  seedSliderTouched(props) {
    Object.assign(this.sliderTouched, seedSliderTouchedKeys(props));
  }

  clearTileValueRevealTimer() {
    clearTimeout(this.releaseTileTimer);
    this.releaseTileTimer = null;
  }

  startTileValueReveal(modeKey) {
    this.clearTileValueRevealTimer();
    this.setState({
      draggingTileKey: null,
      holdingTileKey: null,
      revealingTileKey: modeKey,
      releasingTileKey: null,
    });
    this.releaseTileTimer = setTimeout(() => {
      if (this.unmounted) return;
      this.setState({
        revealingTileKey: null,
        releasingTileKey: modeKey,
      });
      this.releaseTileTimer = setTimeout(() => {
        if (this.unmounted) return;
        this.setState({ releasingTileKey: null });
      }, TILE_VALUE_FADE_MS);
    }, TILE_VALUE_HOLD_MS);
  }

  finishTileInteraction(modeKey, { animateRelease = false } = {}) {
    this.tileDrag = null;
    this.clearTileValueRevealTimer();

    const shouldAnimate =
      animateRelease &&
      modeKey &&
      (this.state.holdingTileKey === modeKey ||
        this.state.draggingTileKey === modeKey ||
        this.state.revealingTileKey === modeKey);

    if (shouldAnimate) {
      this.startTileValueReveal(modeKey);
      return;
    }

    if (
      this.state.draggingTileKey ||
      this.state.holdingTileKey ||
      this.state.revealingTileKey ||
      this.state.releasingTileKey
    ) {
      this.setState({
        draggingTileKey: null,
        holdingTileKey: null,
        revealingTileKey: null,
        releasingTileKey: null,
      });
    }
  }

  updateTileSliderFromPointer(clientX, mode, el) {
    if (!mode.slider || !el) return;

    const value = sliderValueFromClientX(clientX, el, mode.slider);
    this.setTileSliderOverride(mode.key, value);
    mode.slider.onChange(value);
    this.sliderTouched[mode.key] = true;
  }

  setTileSliderOverride(key, value) {
    if (this.state.tileSliderOverrides[key] === value) return;
    this.setState({
      tileSliderOverrides: {
        ...this.state.tileSliderOverrides,
        [key]: value,
      },
    });
  }

  clearTileSliderOverride(key) {
    if (this.state.tileSliderOverrides[key] === undefined) return;
    const tileSliderOverrides = { ...this.state.tileSliderOverrides };
    delete tileSliderOverrides[key];
    this.setState({ tileSliderOverrides });
  }

  clearSettledTileSliderOverrides() {
    const overrides = this.state.tileSliderOverrides;
    const keys = Object.keys(overrides);
    if (!keys.length) return;

    const modes = buildAudioModes(this.props);
    let next = null;
    for (const mode of modes) {
      if (overrides[mode.key] === undefined || !mode.slider) continue;
      if (this.tileDrag?.key === mode.key) continue;
      if (this.state.holdingTileKey === mode.key) continue;
      if (this.state.draggingTileKey === mode.key) continue;
      const step = mode.slider.step || 0.01;
      if (Math.abs(mode.slider.value - overrides[mode.key]) > step / 2) {
        continue;
      }
      if (!next) next = { ...overrides };
      delete next[mode.key];
    }
    if (next) this.setState({ tileSliderOverrides: next });
  }

  applyDefaultSliderIfNeeded(mode) {
    if (!mode.slider || this.sliderTouched[mode.key]) return;

    const defaultVal =
      mode.slider.defaultValue !== undefined
        ? mode.slider.defaultValue
        : sliderMidValue(mode.slider);
    this.setTileSliderOverride(mode.key, defaultVal);
    mode.slider.onChange(defaultVal);
    this.sliderTouched[mode.key] = true;
  }

  onAdjustableTilePointerDown(event, mode) {
    if (this.props.isProFeaturesLocked) return;

    const wasActive = !!mode.active;

    this.clearTileValueRevealTimer();
    this.tileDrag = {
      key: mode.key,
      pointerId: event.pointerId,
      startX: event.clientX,
      dragging: false,
      el: event.currentTarget,
      slider: mode.slider,
      wasActive,
      isActive: wasActive,
      activatedOnDown: false,
    };

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore capture failures in unsupported environments.
    }

    if (!wasActive) {
      // Turn on first, then set level. Firing mode + volume together let an
      // older content-state reply overwrite the newer one in the popup, so
      // older content-state reply overwrite the newer one in the popup, so
      // ambient beds snapped back to 60% or off.
      const setFromClick = mode.slider && !this.sliderTouched[mode.key];
      const onChange = mode.slider?.onChange;
      const volumeFromClick =
        setFromClick && mode.slider
          ? sliderValueFromClientX(
              event.clientX,
              event.currentTarget,
              mode.slider,
            )
          : null;
      if (volumeFromClick != null) {
        this.sliderTouched[mode.key] = true;
        this.setTileSliderOverride(mode.key, volumeFromClick);
      }

      const togglePromise = Promise.resolve(mode.onToggle());
      this.tileDrag.isActive = true;
      this.tileDrag.activatedOnDown = true;
      this.setState(
        {
          holdingTileKey: mode.key,
          revealingTileKey: null,
          releasingTileKey: null,
          fillGrowingKeys: {
            ...this.state.fillGrowingKeys,
            [mode.key]: true,
          },
        },
        () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.clearFillGrow(mode.key);
            });
          });
        },
      );

      void togglePromise.then(() => {
        if (this.unmounted) return;
        if (volumeFromClick != null && onChange) {
          onChange(volumeFromClick);
          return;
        }
        this.applyDefaultSliderIfNeeded(mode);
      });
      return;
    }

    this.setState({
      holdingTileKey: mode.key,
      revealingTileKey: null,
      releasingTileKey: null,
    });
  }

  onAdjustableTilePointerMove(event) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;
    if (!this.tileDrag.isActive) return;

    if (Math.abs(event.clientX - this.tileDrag.startX) <= 4) return;

    if (!this.tileDrag.dragging) {
      this.tileDrag.dragging = true;
      this.clearFillGrow(this.tileDrag.key);
      if (this.state.draggingTileKey !== this.tileDrag.key) {
        this.setState({ draggingTileKey: this.tileDrag.key });
      }
    }

    this.updateTileSliderFromPointer(
      event.clientX,
      { key: this.tileDrag.key, slider: this.tileDrag.slider },
      this.tileDrag.el,
    );
  }

  onAdjustableTilePointerUp(event, mode) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;

    const { dragging, wasActive, activatedOnDown } = this.tileDrag;
    const shouldAnimateRelease =
      (dragging || activatedOnDown) &&
      this.state.holdingTileKey === mode.key;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore release failures in unsupported environments.
    }

    this.finishTileInteraction(mode.key, {
      animateRelease: shouldAnimateRelease,
    });

    this.suppressAdjustableClick = true;

    if (dragging) {
      // Empty end of the track turns the effect off.
      const fillPct =
        this.state.tileSliderOverrides[mode.key] !== undefined
          ? sliderFillPct({
              ...mode.slider,
              value: this.state.tileSliderOverrides[mode.key],
            })
          : sliderFillPct(mode.slider);
      if (mode.active && fillPct <= 0) {
        mode.onToggle();
        delete this.sliderTouched[mode.key];
        this.clearTileSliderOverride(mode.key);
      }
      return;
    }

    if (wasActive) {
      mode.onToggle();
      this.clearTileSliderOverride(mode.key);
    }
  }

  onAdjustableTileClick(mode) {
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    if (this.suppressAdjustableClick) {
      this.suppressAdjustableClick = false;
      return;
    }

    this.handleAdjustableTileTap(mode);
  }

  handleAdjustableTileTap(mode) {
    if (mode.active) {
      mode.onToggle();
      return;
    }

    this.applyDefaultSliderIfNeeded(mode);
    mode.onToggle();
    this.flashTileValue(mode.key);
  }

  flashTileValue(modeKey) {
    this.startTileValueReveal(modeKey);
  }

  onAdjustableTilePointerCancel(event) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;

    const { key, dragging, activatedOnDown } = this.tileDrag;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore capture failures in unsupported environments.
    }

    this.finishTileInteraction(key, {
      animateRelease:
        (dragging || activatedOnDown) && this.state.holdingTileKey === key,
    });
  }

  async runMood(mood) {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      return await this.props.onApplyMood?.(mood);
    } finally {
      this.setState({ shuffleBusy: false });
    }
  }

  async runClearMood() {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      return await this.props.onClearMood?.();
    } finally {
      this.setState({ shuffleBusy: false });
    }
  }

  scheduleSubscriptionStatusRotation() {
    clearTimeout(this.subscriptionStatusTimer);
    const {
      trialMode,
      remainingTrialDays,
      isPro,
      trialEnded,
    } = this.props;
    const hasTrial = trialMode && remainingTrialDays >= 0;
    // Usage mode rotates Unlock PRO → Usage mode → minutes left → Resets
    // weekly, including after the cap so 0 min left still cycles.
    const hasUsage = !hasTrial && trialEnded === true;
    const shouldRotate = !isPro && (hasTrial || hasUsage);
    if (!shouldRotate) {
      this.setState({ subscriptionStatusStep: 0 });
      return;
    }
    // The cap offer, once earned, takes a fifth step and lingers a little
    // longer than the meter readings so the discount actually registers.
    const showPromo =
      hasUsage &&
      Boolean(this.props.usagePromo?.label) &&
      isPromoQualified({
        usagePromo: this.props.usagePromo,
        weeklyCapCount: this.props.weeklyCapCount,
      });
    const dwell = hasUsage
      ? showPromo
        ? [4500, 2400, 2400, 2400, 3200]
        : [4500, 2400, 2400, 2400]
      : [4500, 2400, 2400];
    const stepCount = dwell.length;
    let step = 0;
    this.setState({ subscriptionStatusStep: 0 });
    const tick = () => {
      step = (step + 1) % stepCount;
      this.setState({ subscriptionStatusStep: step });
      this.subscriptionStatusTimer = setTimeout(tick, dwell[step]);
    };
    this.subscriptionStatusTimer = setTimeout(tick, 2000);
  }

  // Off YouTube: the gate's card in the controls' sliding area, so Advanced
  // and Studio open under it and slide up exactly as they do over the
  // controls (setSheetOpen pins this area, scrolls to the sheet, dims the
  // card, and a click on it closes the sheet).
  renderGateBody(card) {
    const { openSheet, contentPushHeight } = this.state;
    const moodOpen = openSheet !== null;
    const { isProFeaturesLocked } = this.props;
    return (
      <div
        className={`content__push-shell${moodOpen ? " content__push-shell--mood-open" : ""}`}
      >
        <div
          className="content__push"
          ref={(el) => {
            this.contentPushRef = el;
          }}
          style={
            moodOpen && contentPushHeight != null
              ? {
                  height: Math.max(
                    contentPushHeight,
                    this.state.sheetMinHeight || 0,
                  ),
                }
              : undefined
          }
        >
          <div className="content__effects-frame">
            <div
              className={`content__effects${moodOpen ? " content__effects--dimmed" : ""}`}
              ref={(el) => this.setContentEffectsRef(el)}
              {...(moodOpen ? { inert: true } : {})}
            >
              {card}
            </div>
            {moodOpen && (
              <button
                type="button"
                className="content__mood-backdrop"
                aria-label="Close panel"
                onClick={() => this.setSheetOpen(null)}
              />
            )}
          </div>

          <div>
            {openSheet === "help" && (
              <HelpSheet
                open
                isProFeaturesLocked={isProFeaturesLocked}
                onChangelogClick={() => this.props.onChangelogClick()}
                onShortcutsClick={() => this.props.onCustomizeShortcut()}
                canCustomizeShortcuts={!isSafari()}
                spotifyBeta={!!this.props.spotifyBeta}
                onSpotifyBetaChange={(on) => this.props.onSpotifyBetaChange?.(on)}
                appleMusicBeta={!!this.props.appleMusicBeta}
                onAppleMusicBetaChange={(on) =>
                  this.props.onAppleMusicBetaChange?.(on)
                }
                bandcampBeta={!!this.props.bandcampBeta}
                onBandcampBetaChange={(on) =>
                  this.props.onBandcampBetaChange?.(on)
                }
              />
            )}
            {openSheet === "studio" && (
              <StudioSheet
                open
                studioUrl={this.props.studioUrl}
                onOpenClick={() => this.props.onAddSongClick?.()}
                onPlaylistClick={() => this.props.onOpenStudioPlaylistClick?.()}
                onHistoryClick={() => this.props.onOpenStudioHistoryClick?.()}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // The controls' area (or the gate's card) can mount after the popup does:
  // the off-YouTube decision comes in later. Its height is watched from
  // whichever element is there now.
  setContentEffectsRef(el) {
    if (el === this.contentEffectsRef) return;
    if (this.contentEffectsRef) {
      this.contentEffectsResizeObserver?.unobserve(this.contentEffectsRef);
    }
    this.contentEffectsRef = el;
    if (el) this.contentEffectsResizeObserver?.observe(el);
  }

  render() {
    const {
      slowControl,
      isSlowedEnabled,
      error,
      notSupported,
      musicProOnly,
      isHeaderVisible,
      isPro,
      trialMode,
      trialEnded,
      skin,
      remainingTrialDays,
      usageHit,
      isFeedbackEnabled,
      isRateAppEnabled,
      isProFeaturesLocked,
      contentApplying,
      billingLoading,
    } = this.props;

    const { openSheet, contentPushHeight, moodScrimTop, moodScrimOpacity } =
      this.state;
    const moodOpen = openSheet !== null;
    // Slowed off: the controls and the bottom bar dim and stop taking clicks.
    const controlsOff = !isSlowedEnabled && !musicProOnly;
    const { debugMode: debugModeEnabled } = this.props;

    if (error) {
      return (
        <div className="popup">
          <div className="content">
            <p>
              {process.env.DEBUG === "true"
                ? error.message
                : "Something went wrong"}
            </p>
          </div>
        </div>
      );
    }

    // Off YouTube the floating panel never mounts — native popup only. Show a
    // compact gate instead of the full controls. It wears Dark when that is
    // the listener's skin and None for every other one (the window-chrome
    // skins assume the in-page rounded host).
    if (notSupported) {
      return (
        <div id="Slowed" className={skin === "dark" ? "dark-mode" : undefined}>
          <div
            className="mood-panel-scrim"
            aria-hidden="true"
            style={{
              opacity: moodScrimOpacity,
              visibility: moodScrimOpacity > 0.01 ? "visible" : "hidden",
              ...(moodScrimTop != null ? { top: `${moodScrimTop}px` } : null),
            }}
          />
          <div className="popup popup--unsupported">
            <UnsupportedGate
              isPro={isPro}
              trialMode={trialMode}
              trialEnded={trialEnded}
              remainingTrialDays={remainingTrialDays}
              usageHit={usageHit}
              usageRemaining={this.props.usageRemaining}
              usagePeriod={this.props.usagePeriod}
              hideManagePlan={this.props.hideManagePlan}
              billingLoading={billingLoading}
              upgradeError={this.props.upgradeError}
              isFeedbackEnabled={isFeedbackEnabled}
              isRateAppEnabled={isRateAppEnabled}
              toolUrl={this.props.toolUrl}
              onAddSongClick={() => this.props.onAddSongClick?.()}
              onPromptSlowIt={(url) => this.props.onPromptSlowIt?.(url)}
              header={
                <PopupHeader
                  skin={skin === "dark" ? "dark" : "color"}
                  isPro={isPro}
                  trialMode={trialMode}
                  trialEnded={trialEnded}
                  remainingTrialDays={remainingTrialDays}
                  usageHit={usageHit}
                  usageRemaining={this.props.usageRemaining}
                  usagePeriod={this.props.usagePeriod}
                  subscriptionStatusStep={this.state.subscriptionStatusStep}
                  usagePromo={this.props.usagePromo}
                  promoQualified={isPromoQualified({
                    usagePromo: this.props.usagePromo,
                    weeklyCapCount: this.props.weeklyCapCount,
                  })}
                  billingLoading={billingLoading}
                  upgradeError={this.props.upgradeError}
                  onUpgradeClick={() => this.props.onUpgradeClick()}
                  onUsageClick={() => this.props.onUsageClick()}
                  onManageSubscriptionClick={() =>
                    this.props.onManageSubscriptionClick()
                  }
                  onDebugToggle={() =>
                    this.props.onDebugModeChange?.(!debugModeEnabled)
                  }
                  showSwitch={false}
                />
              }
              studioUrl={this.props.studioUrl}
              openSheet={openSheet}
              onSheetToggle={(kind) =>
                this.setSheetOpen(openSheet === kind ? null : kind)
              }
              renderBody={(card) => this.renderGateBody(card)}
              dark={skin === "dark"}
              onOpenYouTubeClick={(url) => this.props.onOpenYouTubeClick?.(url)}
              accessSite={this.props.accessSite}
              spotifyBeta={!!this.props.spotifyBeta}
              appleMusicBeta={!!this.props.appleMusicBeta}
              bandcampBeta={!!this.props.bandcampBeta}
              studioIdle={this.props.studioIdle}
              siteAccessBusy={this.props.siteAccessBusy}
              siteAccessError={this.props.siteAccessError}
              onEnableSiteClick={(site) => this.props.onEnableSiteClick?.(site)}
              onUpgradeClick={() => this.props.onUpgradeClick()}
              onManageSubscriptionClick={() =>
                this.props.onManageSubscriptionClick()
              }
              onFeedbackClick={(type) => this.props.onFeedbackClick(type)}
              onHelpClick={() => this.props.onHelpClick()}
              onChangelogClick={() => this.props.onChangelogClick()}
              onRateAppClick={() => this.props.onRateAppClick()}
            />
          </div>
        </div>
      );
    }

    const tileHandlers = {
      onAdjustablePointerDown: (event, mode) =>
        this.onAdjustableTilePointerDown(event, mode),
      onAdjustablePointerMove: (event) =>
        this.onAdjustableTilePointerMove(event),
      onAdjustablePointerUp: (event, mode) =>
        this.onAdjustableTilePointerUp(event, mode),
      onAdjustablePointerCancel: (event) =>
        this.onAdjustableTilePointerCancel(event),
      onAdjustableClick: (mode) => this.onAdjustableTileClick(mode),
    };

    const skinModeClass =
      {
        dark: "dark-mode",
        mono: "mono-mode",
        winamp: "winamp-mode",
        y2k: "y2k-mode",
        neoy2k: "neoy2k-mode",
        matrix: "matrix-mode",
        aquarium: "aquarium-mode",
        dracula: "dracula-mode",
        alien: "alien-mode",
        macos: "macos-mode",
      }[skin] ||
      null;
    const skinClassName = [skinModeClass, moodOpen ? "mood-open" : null]
      .filter(Boolean)
      .join(" ");

    return (
      <div id="Slowed" className={skinClassName || undefined}>
        <div
          className="mood-panel-scrim"
          aria-hidden="true"
          style={{
            opacity: moodScrimOpacity,
            visibility: moodScrimOpacity > 0.01 ? "visible" : "hidden",
            ...(moodScrimTop != null ? { top: `${moodScrimTop}px` } : null),
          }}
        />
        <div
          className={`popup ${this.state.mount ? "" : "mounting"}${
            debugModeEnabled ? " popup--debug" : ""
          }`}
        >
          {isHeaderVisible && (
            <PopupHeader
              announcement={this.props.moodAnnouncement}
              isPro={isPro}
              trialMode={trialMode}
              trialEnded={trialEnded}
              remainingTrialDays={remainingTrialDays}
              usageHit={usageHit}
              usageRemaining={this.props.usageRemaining}
              usagePeriod={this.props.usagePeriod}
              subscriptionStatusStep={this.state.subscriptionStatusStep}
              usagePromo={this.props.usagePromo}
              promoQualified={isPromoQualified({
                usagePromo: this.props.usagePromo,
                weeklyCapCount: this.props.weeklyCapCount,
              })}
              musicProOnly={musicProOnly}
              isSlowedEnabled={isSlowedEnabled}
              contentApplying={contentApplying}
              mount={this.state.mount}
              billingLoading={billingLoading}
              upgradeError={this.props.upgradeError}
              onUpgradeClick={() => this.props.onUpgradeClick()}
              onUsageClick={() => this.props.onUsageClick()}
              onManageSubscriptionClick={() =>
                this.props.onManageSubscriptionClick()
              }
              onProClick={() => this.openBilling()}
              onDebugToggle={() =>
                this.props.onDebugModeChange?.(!debugModeEnabled)
              }
              onSlowedToggle={(value) => this.props.onSlowedToggle(value)}
              offersMini={!isSafari()}
              onMiniMinimize={() => this.props.onMiniMinimize?.()}
            />
          )}

          <div
            className={`content${musicProOnly ? " content--disabled" : ""}`}
          >
            {musicProOnly ? (
              <div className="content__overlay content__overlay--music-pro">
                <p className="content__overlay-title">YouTube Music</p>
                <p className="content__overlay-text">
                  Slowing tracks on{" "}
                  <strong>music.youtube.com</strong> is included with Slowed Pro.
                </p>
                <button
                  type="button"
                  className="content__overlay-cta"
                  onClick={() => this.props.onUpgradeClick()}
                >
                  Unlock Pro
                </button>
              </div>
            ) : null}

            <div
              className={`content__push-shell${moodOpen ? " content__push-shell--mood-open" : ""}`}
            >
              <div
                className="content__push"
                ref={(el) => {
                  this.contentPushRef = el;
                }}
                style={
                  moodOpen && contentPushHeight != null
                    ? {
                        height: Math.max(
                          contentPushHeight,
                          this.state.sheetMinHeight || 0,
                        ),
                      }
                    : undefined
                }
              >
                <div className="content__effects-frame">
                  <div
                    className={`content__effects${controlsOff ? " content--disabled" : ""}${moodOpen ? " content__effects--dimmed" : ""}`}
                    ref={(el) => {
                      this.contentEffectsRef = el;
                    }}
                    {...(moodOpen ? { inert: true } : {})}
                  >
                    {controlsOff && (
                      <div className="content__overlay" />
                    )}

                    <SlowControlSection
                      slowControl={slowControl}
                      speedUpMode={!!this.props.speedUpMode}
                      pitchLocked={!!this.props.preservePitchMode}
                      isProFeaturesLocked={isProFeaturesLocked}
                      showReset={shouldShowResetAll(this.props)}
                      controlsAtDefaults={areControlsAtDefaults(this.props)}
                      onSlowControlChange={(value) =>
                        this.props.onSlowControlChange(value)
                      }
                      onPitchLockChange={(value) =>
                        this.props.onPreservePitchModeChange(value)
                      }
                      onSpeedUpModeChange={(value) =>
                        this.props.onSpeedUpModeChange(value)
                      }
                      onResetClick={() => this.props.onResetControls()}
                      onUpgradeClick={() => this.props.onUpgradeClick()}
                    />

                    <EffectsSection
                      props={this.props}
                      tileState={this.state}
                      tileHandlers={tileHandlers}
                    />
                  </div>

                  {moodOpen && (
                    <button
                      type="button"
                      className="content__mood-backdrop"
                      aria-label="Close panel"
                      onClick={() => this.setSheetOpen(null)}
                    />
                  )}
                </div>

                <div>
                {openSheet === "mood" && (
                  <Mood
                    open
                    isProFeaturesLocked={isProFeaturesLocked}
                    selectedMoodName={this.props.selectedMoodName}
                    speedUpMode={
                      this.props.speedUpMode && !isProFeaturesLocked
                    }
                    trackName={this.props.trackName}
                    onUpgradeClick={() => this.props.onUpgradeClick()}
                    onApplyMood={(mood) => this.runMood(mood)}
                    onClearMood={() => this.runClearMood()}
                    onOpen={(metadata) => this.props.onMoodOpen(metadata)}
                    onLockedClick={(metadata) =>
                      this.props.onMoodLockedClick(metadata)
                    }
                  />
                )}

                {openSheet === "skin" && (
                  <SkinSheet
                    open
                    skin={skin}
                    isProFeaturesLocked={isProFeaturesLocked}
                    debugMode={debugModeEnabled}
                    onSkinChange={(value) => this.props.onSkinChange(value)}
                    onUpgradeClick={() => this.props.onUpgradeClick()}
                  />
                )}

                {openSheet === "help" && (
                  <HelpSheet
                    open
                    isProFeaturesLocked={isProFeaturesLocked}
                    onChangelogClick={() => this.props.onChangelogClick()}
                    onShortcutsClick={() => this.props.onCustomizeShortcut()}
                    canCustomizeShortcuts={!isSafari()}
                    spotifyBeta={!!this.props.spotifyBeta}
                    onSpotifyBetaChange={(on) =>
                      this.props.onSpotifyBetaChange?.(on)
                    }
                    appleMusicBeta={!!this.props.appleMusicBeta}
                    onAppleMusicBetaChange={(on) =>
                      this.props.onAppleMusicBetaChange?.(on)
                    }
                    bandcampBeta={!!this.props.bandcampBeta}
                    onBandcampBetaChange={(on) =>
                      this.props.onBandcampBetaChange?.(on)
                    }
                  />
                )}

                {openSheet === "feedback" && (
                  <FeedbackSheet
                    open
                    isFeedbackEnabled={isFeedbackEnabled}
                    isRateAppEnabled={isRateAppEnabled}
                    onFeedbackClick={(type) =>
                      this.props.onFeedbackClick(type)
                    }
                    onFaqClick={() => this.props.onHelpClick()}
                    onRateAppClick={() => this.props.onRateAppClick()}
                    onAboutClick={() => this.props.onAboutClick()}
                  />
                )}

                {openSheet === "billing" && (
                  <BillingSheet
                    open
                    billing={this.props.billing}
                    onLinkClick={(url) => this.props.onBillingLinkClick?.(url)}
                    onManageSubscriptionClick={() =>
                      this.props.onManageSubscriptionClick()
                    }
                  />
                )}

                {openSheet === "studio" && (
                  <StudioSheet
                    open
                    studioUrl={this.props.studioUrl}
                    studioSaveUrl={this.props.studioSaveUrl}
                    onOpenClick={this.props.onStudioClick}
                    saved={this.props.studioSongSaved}
                    onSaveClick={this.props.onStudioSaveClick}
                    onRemoveClick={this.props.onStudioRemoveClick}
                    onHistoryClick={this.props.onStudioHistoryClick}
                  />
                )}
                </div>
              </div>
            </div>

            {this.props.updateBannerVisible && (
              <div className="rate-banner update-banner" role="status">
                <span className="update-banner__icon" aria-hidden="true">
                  <svg viewBox="0 0 12 12" width="13" height="13">
                    <path d="M6 .5l1.3 3.4 3.4 1.3-3.4 1.3L6 9.9 4.7 6.5 1.3 5.2l3.4-1.3z" />
                    <path d="M10 8.3l.5 1.2 1.2.5-1.2.5-.5 1.2-.5-1.2-1.2-.5 1.2-.5z" />
                  </svg>
                </span>
                <span className="rate-banner__text">
                  {this.props.updateBannerText}
                </span>
                <div className="rate-banner__actions">
                  <button
                    type="button"
                    className="rate-banner__button"
                    onClick={() => this.props.onUpdateBannerDismiss?.()}
                  >
                    Got it
                  </button>
                  <button
                    type="button"
                    className="rate-banner__button rate-banner__button--primary"
                    onClick={() => this.props.onUpdateBannerClick?.()}
                  >
                    {this.props.updateBannerCta}
                  </button>
                </div>
              </div>
            )}

            {this.props.ratePromptVisible && !this.props.updateBannerVisible && (
              <div className="rate-banner" role="status">
                <span className="rate-banner__stars" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <svg key={i} viewBox="0 0 12 12" width="11" height="11">
                      <path d="M6 .8l1.6 3.3 3.6.5-2.6 2.5.6 3.6L6 9l-3.2 1.7.6-3.6L.8 4.6l3.6-.5z" />
                    </svg>
                  ))}
                </span>
                <span className="rate-banner__text">
                  Enjoying Slowed?
                </span>
                <div className="rate-banner__actions">
                  <button
                    type="button"
                    className="rate-banner__button"
                    onClick={() => this.props.onRatePromptDismiss?.()}
                  >
                    Not now
                  </button>
                  <button
                    type="button"
                    className="rate-banner__button rate-banner__button--primary"
                    onClick={() => this.props.onRatePromptClick?.()}
                  >
                    Rate it
                  </button>
                </div>
              </div>
            )}

            {this.props.promoNudge &&
              PROMO_NUDGE_ICONS[this.props.promoNudge] &&
              !this.props.updateBannerVisible &&
              !this.props.ratePromptVisible && (
                <div
                  className={`rate-banner promo-nudge promo-nudge--${this.props.promoNudge}`}
                  role="status"
                >
                  <span className="promo-nudge__icon" aria-hidden="true">
                    <svg viewBox="0 0 12 12" width="13" height="13">
                      {PROMO_NUDGE_ICONS[this.props.promoNudge]}
                    </svg>
                  </span>
                  <span className="rate-banner__text">
                    {this.props.promoNudgeText}
                  </span>
                  <div className="rate-banner__actions">
                    <button
                      type="button"
                      className="rate-banner__button"
                      onClick={() => this.props.onPromoNudgeDismiss?.()}
                    >
                      Not now
                    </button>
                    <button
                      type="button"
                      className="rate-banner__button rate-banner__button--primary"
                      onClick={() => this.props.onPromoNudgeClick?.()}
                    >
                      {this.props.promoNudgeCta}
                    </button>
                  </div>
                </div>
              )}

            <div
              className={`content__footer${controlsOff ? " content--disabled" : ""}`}
            >
              {controlsOff && <div className="content__overlay" />}
              <div {...(controlsOff ? { inert: true } : {})}>
                <ActionsFooter
                  activeSheet={openSheet}
                  hiddenActions={
                    // The moods are effects; where there are none (Spotify), so
                    // is the way to them.
                    this.props.hasEffects === false
                      ? [...(this.props.hiddenActions || []), "mood"]
                      : this.props.hiddenActions
                  }
                  studioUrl={this.props.studioUrl}
                  onStudioClick={() =>
                    this.setSheetOpen(openSheet === "studio" ? null : "studio")
                  }
                  onMoodClick={() =>
                    this.setSheetOpen(openSheet === "mood" ? null : "mood")
                  }
                  onSkinClick={() =>
                    this.setSheetOpen(openSheet === "skin" ? null : "skin")
                  }
                  onFeedbackClick={() =>
                    this.setSheetOpen(openSheet === "feedback" ? null : "feedback")
                  }
                  onHelpClick={() =>
                    this.setSheetOpen(openSheet === "help" ? null : "help")
                  }
                />
              </div>
            </div>
          </div>
        </div>

          {debugModeEnabled && (
            <DebugBar
              testMode={this.props.testMode}
              paymentHookId={this.props.paymentHookId}
              hookBusy={this.props.hookBusy}
              hookError={this.props.hookError}
              isPro={isPro}
              trialMode={trialMode}
              usageHit={this.props.usageHit}
              usageProgress={this.props.usageProgress}
              onTestModeChange={(value) => this.props.onTestModeChange(value)}
              sendAnalytics={this.props.sendAnalytics}
              onSendAnalyticsChange={(value) =>
                this.props.onSendAnalyticsChange?.(value)
              }
              onRefreshPaymentHookClick={() =>
                this.props.onRefreshPaymentHookClick()
              }
              onStartTrialModeClick={() => this.props.onStartTrialModeClick()}
              onUsingUsageClick={() => this.props.onUsingUsageClick()}
              onForceMaxUsageClick={() => this.props.onForceMaxUsageClick()}
              onUpgradeClick={() => this.props.onUpgradeClick()}
              nudgePreview={this.props.nudgePreview}
              onNudgePreviewChange={(key, value) =>
                this.props.onNudgePreviewChange?.(key, value)
              }
              promoPreviewNote={this.props.promoPreviewNote}
              capNoticeStatus={this.props.capNoticeStatus}
              onCapNoticePreview={(period) =>
                this.props.onCapNoticePreview?.(period)
              }
              onCapNoticeReset={() => this.props.onCapNoticeReset?.()}
            />
          )}
      </div>
    );
  }
}

PopupComponent.defaultProps = popupComponentDefaultProps;

self.Slowed.Components.Popup = PopupComponent;

export default PopupComponent;
